// =============================================
// BulkAutoGenerateProgress — floating overlay z postępem bulk auto-generate
// =============================================
// STOP-F. Pojawia sie gdy useBulkAutoGenerateContext().running === true.
// Po zakonczeniu (phase=done) pokazuje summary z retry failed button.
//
// Wzor uproszczony — sekwencyjnie, jedna mapa naraz (NIE active slots multi-pool).
// =============================================

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Pause,
  Play,
  Square,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Loader2,
} from "lucide-react";
import { useBulkAutoGenerateContext } from "@/contexts/BulkAutoGenerateContext";
import { useThematicMaps } from "@/hooks/useThematicMaps";

export function BulkAutoGenerateProgress() {
  const { running, progress, summary, pause, resume, stop, start } =
    useBulkAutoGenerateContext();
  const { maps } = useThematicMaps();
  const [minimized, setMinimized] = useState(false);
  const [showSummary, setShowSummary] = useState(true);

  // Lookup mapy po ID (cached przez React Query w useThematicMaps)
  const currentMapName = progress.currentMapId
    ? maps.find((m) => m.id === progress.currentMapId)?.name
    : undefined;
  const failedWithNames = summary
    ? summary.failed.map((f) => ({
        ...f,
        name: maps.find((m) => m.id === f.mapId)?.name,
      }))
    : [];

  // Show overlay gdy running ALBO summary istnieje (post-run)
  if (!running && !summary) return null;
  if (!showSummary && !running) return null;

  // Pasek liczy frakcjonalnie: (mapy_done + procent_obecnej_mapy) / total
  // np. 0 map done + 3/6 stages w obecnej = 0.5/1 mapy = 50% dla N=1
  const stagesTotal = Math.max(1, progress.currentMapStagesTotal);
  const currentMapFraction = running
    ? progress.currentMapStagesCompleted / stagesTotal
    : 0;
  const pct =
    progress.total > 0
      ? Math.round(
          ((progress.done + currentMapFraction) / progress.total) * 100,
        )
      : 0;

  const handleRetryFailed = async () => {
    if (!summary || summary.failed.length === 0) return;
    const failedIds = summary.failed.map((f) => f.mapId);
    // NOTE: opts re-use ostatnie z start() — UI nie trzyma ich tu, wiec defaultem
    // bulk_done summary nie ma options. Re-uruchom z defaultami (relations + serp + skipDone).
    await start(failedIds, {
      targetStage: "relations",
      skipDone: true,
      withSerpValidation: true,
    });
  };

  const isActive = running && progress.phase === "running";

  return (
    <div
      className="fixed right-4 z-50 w-[420px] rounded-lg border bg-card shadow-2xl"
      style={{ bottom: "76px" }} // 4 (AppLogFab bottom) + 11 (FAB height) + 8 spacing = ~76px
    >
      {/* Header — caly klikalny dla toggle minimize (rozwiazuje bug "nie moge otworzyc po minimize") */}
      <button
        type="button"
        onClick={() => setMinimized(!minimized)}
        className="w-full flex items-center justify-between p-3 border-b hover:bg-accent/50 transition cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {isActive ? (
            <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
          ) : (
            <Zap
              className={`h-4 w-4 ${progress.phase === "done" && summary?.failed.length === 0 ? "text-emerald-500" : "text-amber-500"}`}
            />
          )}
          <span className="font-semibold text-sm">
            {progress.phase === "done"
              ? summary && summary.failed.length === 0
                ? "AUTO-GENERATE — gotowe ✓"
                : "AUTO-GENERATE — zakonczone"
              : isActive
                ? `AUTO-GENERATE • ${progress.currentStage || "..."}`
                : progress.phase === "paused"
                  ? "AUTO-GENERATE • PAUZA"
                  : "AUTO-GENERATE MAPS"}
          </span>
          {/* Counter widoczny nawet w minimized — user widzi postep bez rozwijania */}
          {isActive && (
            <span className="text-xs text-muted-foreground">
              {progress.done}/{progress.total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!running && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setShowSummary(false);
              }}
              className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          {minimized ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </button>

      {/* Body */}
      {!minimized && (
        <div className="p-3 space-y-3 max-h-[500px] overflow-y-auto">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-md bg-muted/50 p-2">
              <div className="font-semibold text-emerald-600">{progress.done}</div>
              <div className="text-muted-foreground">OK</div>
            </div>
            <div className="rounded-md bg-muted/50 p-2">
              <div className="font-semibold text-red-600">{progress.failed}</div>
              <div className="text-muted-foreground">Failed</div>
            </div>
            <div className="rounded-md bg-muted/50 p-2">
              <div className="font-semibold">{progress.total}</div>
              <div className="text-muted-foreground">Total</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Postep</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Cleanup info */}
          {progress.cleanedLocks != null && progress.cleanedLocks > 0 && (
            <div className="text-[11px] text-muted-foreground">
              Cleanup: zwolniono {progress.cleanedLocks} stale locks (&gt;60 min)
            </div>
          )}

          {/* Aktualna mapa */}
          {running && progress.currentMapId && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2 space-y-1">
              <div className="text-xs font-medium flex items-center gap-2 flex-wrap">
                <Loader2 className="h-3 w-3 animate-spin text-emerald-500 shrink-0" />
                {currentMapName && (
                  <span className="font-semibold">{currentMapName}</span>
                )}
                <span className="font-mono text-[10px] text-muted-foreground">
                  ({progress.currentMapId.slice(0, 8)})
                </span>
              </div>
              {progress.currentStage && (
                <div className="text-xs flex items-center gap-2 pl-5 flex-wrap">
                  <Badge
                    variant="outline"
                    className="text-[10px] border-emerald-500/50"
                  >
                    stage: {progress.currentStage}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">
                    Etap {progress.currentMapStagesCompleted + 1}/
                    {progress.currentMapStagesTotal}
                  </span>
                  {progress.lastMsg && (
                    <span className="text-muted-foreground text-[11px] truncate">
                      {progress.lastMsg}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Phase indicator */}
          {progress.phase === "paused" && (
            <div className="text-xs text-amber-600 flex items-center gap-2">
              <Pause className="h-3 w-3" />
              Wstrzymano (pauza zacznie obowiazywac po obecnym etapie)
            </div>
          )}
          {progress.phase === "stopping" && (
            <div className="text-xs text-red-600 flex items-center gap-2">
              <Square className="h-3 w-3" />
              Zatrzymywanie — biezaca mapa dokonczy obecny stage
            </div>
          )}

          {/* Summary po koncu */}
          {summary && progress.phase === "done" && (
            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center gap-2">
                {summary.failed.length === 0 ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                )}
                <span className="text-sm font-medium">Podsumowanie</span>
              </div>
              <div className="text-xs space-y-1">
                <div>
                  Calkowity koszt:{" "}
                  <strong>${summary.totalCostUsd.toFixed(4)}</strong>
                </div>
                <div>
                  Czas:{" "}
                  <strong>
                    {(summary.durationMs / 1000).toFixed(1)}s
                  </strong>
                </div>
                {summary.stopped && (
                  <div className="text-amber-600">Zatrzymano przez user</div>
                )}
              </div>

              {/* Failed maps lista */}
              {summary.failed.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-red-600 font-medium">
                    {summary.failed.length} map z bledem
                  </summary>
                  <div className="mt-2 space-y-2 max-h-[150px] overflow-y-auto">
                    {failedWithNames.map((f) => (
                      <div
                        key={f.mapId}
                        className="rounded border border-red-200 dark:border-red-900 p-2 text-[11px]"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          {f.name && (
                            <span className="font-semibold">{f.name}</span>
                          )}
                          <span className="font-mono text-[10px] text-muted-foreground">
                            ({f.mapId.slice(0, 8)})
                          </span>
                        </div>
                        <div className="text-muted-foreground">
                          stage: {f.errorStage || "?"}
                        </div>
                        <div className="text-red-600 dark:text-red-400 break-words">
                          {f.error}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {summary.failed.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleRetryFailed}
                >
                  Retry failed maps ({summary.failed.length})
                </Button>
              )}
            </div>
          )}

          {/* Controls (tylko gdy running) */}
          {running && (
            <div className="flex items-center gap-2 border-t pt-3">
              {progress.phase !== "paused" ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={pause}
                  className="flex-1"
                >
                  <Pause className="h-3 w-3 mr-1" />
                  Pauza
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resume}
                  className="flex-1"
                >
                  <Play className="h-3 w-3 mr-1" />
                  Wznow
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={stop}
                className="flex-1"
              >
                <Square className="h-3 w-3 mr-1" />
                Stop
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
